import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Skeleton, Typography } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { WalletModelShort } from '@root/entities/wallet/ui'
import { useTranslation } from '@root/shared/localization'

import type { TransferFlowReatom } from '../../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: TransferFlowReatom
} & ClassNameProps

export const TransferReviewHeader: React.FC<Props> = ({ className, model }) => {
  const { t } = useTranslation()

  const wallet = model.wallet
  const [address] = useAtom(model.destination.addressAtom)
  const [currency] = useAtom(model.currency.currencyAtom)
  const [amount] = useAtom(model.amount.amountAtom)
  const [amountUsd] = useAtom(model.amount.amountUsdStateAtom)

  return (
    <Root className={className}>
      <WalletContainer>
        <WalletModelShort wallet={wallet} />
      </WalletContainer>
      <SendTo>{t('transfer.send-to')}</SendTo>
      <Address>{address}</Address>
      <CurrencyContainer>
        <CurrencyIcon currency={currency} size={24} />
        <span>{currency.title}</span>
      </CurrencyContainer>
      <Amount>
        <Numeric value={amount} preset="currency-amount-short" />
        <Ticker> {currency.ticker}</Ticker>
      </Amount>
      <AmountUsd>
        {amountUsd.value ? (
          <Numeric
            value={amountUsd.value}
            preset="currency-amount-short"
            currency="$"
          />
        ) : (
          <Skeleton height={12} width={40} paddingBlock={1} />
        )}
      </AmountUsd>
    </Root>
  )
}

const Root = styled.div``

const WalletContainer = styled.div`
  display: grid;
  justify-content: center;
  padding-top: 12px;
  margin-bottom: 12px;
`

const SendTo = styled(Typography).attrs({
  variant: 'body4-regular',
  component: 'div',
})`
  color: var(--text-9);
  text-align: center;
  margin-bottom: 12px;
`

const Address = styled(Typography).attrs({
  variant: 'body4-regular',
  component: 'div',
})`
  height: 48px;
  padding-inline: 12px;
  border-radius: 12px;
  overflow-x: auto;
  text-align: center;

  border: 1px solid rgba(var(--background-12-rgb), 0.05);
  background: rgba(var(--appearance-1-rgb), 0.3);

  display: grid;
  align-items: center;

  color: var(--text-12);

  margin-bottom: 16px;
`

const CurrencyContainer = styled(Typography).attrs({
  variant: 'body4-bold',
  component: 'div',
})`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  justify-content: center;
  gap: 7px;

  color: var(--text-12);
  text-align: center;

  margin-bottom: 8px;
`

const Amount = styled(Typography).attrs({
  variant: 'h4',
  component: 'div',
})`
  color: var(--text-12);
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  line-height: 24px;
  margin-bottom: 4px;
`

const AmountUsd = styled(Typography).attrs({
  variant: 'description-semi-bold',
  component: 'div',
})`
  color: var(--text-9);
  text-align: center;

  display: grid;
  justify-content: center;

  margin-bottom: 12px;
`

const Ticker = styled(Typography).attrs({
  variant: 'body1-medium',
  component: 'span',
})``
